import { api } from './api';
import type {
  VacanciesResponse,
  VacancyDetail,
  VacancyQueryParams,
  VacancyStatus,
  CreateVacancyInput,
  UpdateVacancyInput,
  Skill,
  PipelineTemplate,
  VacancyPipelineStage,
  AddPipelineStageInput,
  UpdatePipelineStageInput,
  ReorderPipelineStagesInput,
  CreatePipelineTemplateInput,
  UpdatePipelineTemplateInput,
  PublicVacancy,
  PipelineResponse,
  BulkAssignCandidatesInput,
  BulkAssignCandidatesResult,
  BulkAssignCandidatesMultiInput,
  BulkAssignCandidatesMultiResult,
  RejectionReason,
  CreateRejectionReasonInput,
  UpdateRejectionReasonInput,
  SkillAdminListItem,
  SkillAdminDetail,
  SkillMergePreview,
  SkillAlias,
} from '@/types/recruitment';

// Vacancies
export async function getVacancies(params: VacancyQueryParams = {}): Promise<VacanciesResponse> {
  const { data } = await api.get<VacanciesResponse>('/recruitment/vacancies', { params });
  return data;
}

export async function getVacancy(id: string): Promise<VacancyDetail> {
  const { data } = await api.get<VacancyDetail>(`/recruitment/vacancies/${id}`);
  return data;
}

export async function createVacancy(input: CreateVacancyInput): Promise<VacancyDetail> {
  const { data } = await api.post<VacancyDetail>('/recruitment/vacancies', input);
  return data;
}

export async function updateVacancy(id: string, input: UpdateVacancyInput): Promise<VacancyDetail> {
  const { data } = await api.patch<VacancyDetail>(`/recruitment/vacancies/${id}`, input);
  return data;
}

export async function deleteVacancy(id: string): Promise<void> {
  await api.delete(`/recruitment/vacancies/${id}`);
}

export async function duplicateVacancy(id: string): Promise<VacancyDetail> {
  const { data } = await api.post<VacancyDetail>(`/recruitment/vacancies/${id}/duplicate`);
  return data;
}

export async function archiveVacancy(id: string): Promise<VacancyDetail> {
  const { data } = await api.patch<VacancyDetail>(`/recruitment/vacancies/${id}/archive`);
  return data;
}

export async function restoreVacancy(id: string): Promise<VacancyDetail> {
  const { data } = await api.patch<VacancyDetail>(`/recruitment/vacancies/${id}/restore`);
  return data;
}

export async function updateVacancyStatus(
  id: string,
  status: VacancyStatus,
): Promise<{ id: string; status: VacancyStatus }> {
  const { data } = await api.patch<{ id: string; status: VacancyStatus }>(
    `/recruitment/vacancies/${id}/status`,
    { status },
  );
  return data;
}

export interface ReorderCandidateItem {
  id: string;
  sortOrder: number;
  stageId?: string;
  /** Required when the card moves into an ARCHIVED-mapped stage (HW-494). */
  rejectionReasonId?: string;
  /** Optional free-text comment accompanying the structured rejection reason. */
  rejectionComment?: string;
}

export async function reorderCandidates(
  vacancyId: string,
  items: ReorderCandidateItem[],
): Promise<void> {
  await api.patch(`/recruitment/vacancies/${vacancyId}/candidates/reorder`, { items });
}

export interface ConvertCandidateToEmployeePayload {
  vacancyId: string;
  firstName: string;
  lastName: string;
  workEmail: string;
  personalEmail?: string;
  startDate: string;
  preferredName?: string;
  positionId?: string;
  departmentId?: string;
  divisionId?: string;
  managerId?: string;
  managerIds?: string[];
  seniorityLevelId?: string;
  employmentTypeId?: string;
  employmentType?: string;
  probationEndDate?: string;
  nextReviewDate?: string;
  roleId?: string;
  avatarUrl?: string;
  rejectOtherAssignments?: boolean;
}

export interface ConvertCandidateToEmployeeResult {
  employeeId: string;
  candidateId: string;
  assignmentId: string;
  rejectedOthers: number;
}

export async function convertCandidateToEmployee(
  candidateId: string,
  body: ConvertCandidateToEmployeePayload,
): Promise<ConvertCandidateToEmployeeResult> {
  const { data } = await api.post<ConvertCandidateToEmployeeResult>(
    `/recruitment/candidates/${candidateId}/convert-to-employee`,
    body,
  );
  return data;
}

export async function getVacancyPipeline(id: string): Promise<PipelineResponse> {
  const { data } = await api.get<PipelineResponse>(`/recruitment/vacancies/${id}/pipeline`);
  return data;
}

// Rejection reasons (HW-494)
export async function getRejectionReasons(): Promise<RejectionReason[]> {
  const { data } = await api.get<{ data: RejectionReason[] }>('/recruitment/rejection-reasons');
  return data.data;
}

export async function getAllRejectionReasons(): Promise<RejectionReason[]> {
  const { data } = await api.get<{ data: RejectionReason[] }>(
    '/recruitment/rejection-reasons/all',
  );
  return data.data;
}

export async function createRejectionReason(
  input: CreateRejectionReasonInput,
): Promise<RejectionReason> {
  const { data } = await api.post<RejectionReason>('/recruitment/rejection-reasons', input);
  return data;
}

export async function updateRejectionReason(
  id: string,
  input: UpdateRejectionReasonInput,
): Promise<RejectionReason> {
  const { data } = await api.patch<RejectionReason>(
    `/recruitment/rejection-reasons/${id}`,
    input,
  );
  return data;
}

export async function bulkAssignCandidatesToVacancy(
  vacancyId: string,
  input: BulkAssignCandidatesInput,
): Promise<BulkAssignCandidatesResult> {
  const { data } = await api.post<BulkAssignCandidatesResult>(
    `/recruitment/vacancies/${vacancyId}/assignments/bulk`,
    input,
  );
  return data;
}

export async function bulkAssignCandidatesMulti(
  input: BulkAssignCandidatesMultiInput,
): Promise<BulkAssignCandidatesMultiResult> {
  const { data } = await api.post<BulkAssignCandidatesMultiResult>(
    '/recruitment/assignments/bulk',
    input,
  );
  return data;
}

// Skills
export async function searchSkills(search: string, limit = 20): Promise<Skill[]> {
  const { data } = await api.get<{ data: Skill[] }>('/recruitment/skills', {
    params: { search, limit },
  });
  return data.data;
}

// Skills admin (HW-530)
export async function listSkillsAdmin(search?: string): Promise<SkillAdminListItem[]> {
  const { data } = await api.get<{ data: SkillAdminListItem[] }>('/recruitment/skills-admin', {
    params: search ? { search } : undefined,
  });
  return data.data;
}

export async function getSkillAdmin(id: string): Promise<SkillAdminDetail> {
  const { data } = await api.get<SkillAdminDetail>(`/recruitment/skills-admin/${id}`);
  return data;
}

export async function createSkillAdmin(name: string): Promise<SkillAdminDetail> {
  const { data } = await api.post<SkillAdminDetail>('/recruitment/skills-admin', { name });
  return data;
}

export async function renameSkillAdmin(id: string, name: string): Promise<SkillAdminDetail> {
  const { data } = await api.patch<SkillAdminDetail>(`/recruitment/skills-admin/${id}`, { name });
  return data;
}

export async function deleteSkillAdmin(id: string): Promise<void> {
  await api.delete(`/recruitment/skills-admin/${id}`);
}

export async function addSkillAlias(skillId: string, alias: string): Promise<SkillAlias> {
  const { data } = await api.post<SkillAlias>(
    `/recruitment/skills-admin/${skillId}/aliases`,
    { alias },
  );
  return data;
}

export async function removeSkillAlias(skillId: string, aliasId: string): Promise<void> {
  await api.delete(`/recruitment/skills-admin/${skillId}/aliases/${aliasId}`);
}

export async function previewSkillMerge(
  loserId: string,
  winnerId: string,
): Promise<SkillMergePreview> {
  const { data } = await api.get<SkillMergePreview>(
    `/recruitment/skills-admin/${loserId}/merge-preview`,
    { params: { winnerId } },
  );
  return data;
}

export async function mergeSkills(loserId: string, winnerId: string): Promise<void> {
  await api.post(`/recruitment/skills-admin/${loserId}/merge-into`, { winnerId });
}

// Pipeline Templates
export async function getPipelineTemplates(): Promise<PipelineTemplate[]> {
  const { data } = await api.get<{ data: PipelineTemplate[] }>('/recruitment/pipeline-templates');
  return data.data;
}

export async function getPipelineTemplate(id: string): Promise<PipelineTemplate> {
  const { data } = await api.get<PipelineTemplate>(`/recruitment/pipeline-templates/${id}`);
  return data;
}

export async function createPipelineTemplate(input: CreatePipelineTemplateInput): Promise<PipelineTemplate> {
  const { data } = await api.post<PipelineTemplate>('/recruitment/pipeline-templates', input);
  return data;
}

export async function updatePipelineTemplate(id: string, input: UpdatePipelineTemplateInput): Promise<PipelineTemplate> {
  const { data } = await api.patch<PipelineTemplate>(`/recruitment/pipeline-templates/${id}`, input);
  return data;
}

export async function deletePipelineTemplate(id: string): Promise<void> {
  await api.delete(`/recruitment/pipeline-templates/${id}`);
}

export async function addTemplateStage(templateId: string, input: AddPipelineStageInput): Promise<PipelineTemplate> {
  const { data } = await api.post<PipelineTemplate>(`/recruitment/pipeline-templates/${templateId}/stages`, input);
  return data;
}

export async function updateTemplateStage(templateId: string, stageId: string, input: UpdatePipelineStageInput): Promise<PipelineTemplate> {
  const { data } = await api.patch<PipelineTemplate>(`/recruitment/pipeline-templates/${templateId}/stages/${stageId}`, input);
  return data;
}

export async function deleteTemplateStage(templateId: string, stageId: string): Promise<PipelineTemplate> {
  const { data } = await api.delete<PipelineTemplate>(`/recruitment/pipeline-templates/${templateId}/stages/${stageId}`);
  return data;
}

export async function reorderTemplateStages(templateId: string, input: ReorderPipelineStagesInput): Promise<PipelineTemplate> {
  const { data } = await api.put<PipelineTemplate>(`/recruitment/pipeline-templates/${templateId}/stages/reorder`, input);
  return data;
}

// Pipeline Stages
export async function addPipelineStage(vacancyId: string, input: AddPipelineStageInput): Promise<VacancyPipelineStage[]> {
  const { data } = await api.post<VacancyPipelineStage[]>(`/recruitment/vacancies/${vacancyId}/pipeline`, input);
  return data;
}

export async function updatePipelineStage(vacancyId: string, stageId: string, input: UpdatePipelineStageInput): Promise<VacancyPipelineStage> {
  const { data } = await api.patch<VacancyPipelineStage>(`/recruitment/vacancies/${vacancyId}/pipeline/${stageId}`, input);
  return data;
}

export async function deletePipelineStage(vacancyId: string, stageId: string): Promise<VacancyPipelineStage[]> {
  const { data } = await api.delete<VacancyPipelineStage[]>(`/recruitment/vacancies/${vacancyId}/pipeline/${stageId}`);
  return data;
}

export async function reorderPipelineStages(vacancyId: string, input: ReorderPipelineStagesInput): Promise<VacancyPipelineStage[]> {
  const { data } = await api.put<VacancyPipelineStage[]>(`/recruitment/vacancies/${vacancyId}/pipeline/reorder`, input);
  return data;
}

// Public endpoints (no auth)
export interface PublicVacancyListItem {
  id: string;
  title: string;
  publicSlug: string;
  location: { name: string } | null;
  department: { name: string } | null;
}

export async function listPublicVacancies(excludeSlug?: string): Promise<PublicVacancyListItem[]> {
  const params = excludeSlug ? { exclude: excludeSlug } : {};
  const { data } = await api.get<PublicVacancyListItem[]>('/recruitment/public/vacancies', { params });
  return data;
}

export async function getPublicVacancy(slug: string): Promise<PublicVacancy> {
  const { data } = await api.get<PublicVacancy>(`/recruitment/public/vacancies/${slug}`);
  return data;
}

export async function submitApplication(slug: string, formData: FormData): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(`/recruitment/public/apply/${slug}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
